import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, Settings } from 'lucide-react'
import { useNotifications } from '../../../Context/NotificationContext'
import { appName } from '../../../Constant'
import './TopHeader.css'

function timeAgo(isoString) {
  if (!isoString) return ''
  const diff = Date.now() - new Date(isoString).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

export default function TopHeader() {
  const navigate = useNavigate()
  const { notifications, unreadCount, markAsRead } = useNotifications()
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleMarkAsRead = async (e, notificationId) => {
    e.stopPropagation()
    await markAsRead(notificationId)
  }

  const handleMarkAllRead = async () => {
    const unread = notifications.filter((n) => !n.isRead)
    await Promise.all(unread.map((n) => markAsRead(n.notificationId)))
  }

  return (
    <header className="top-header">
      <div className="top-header-left">
        <span className="app-name">
          <span className="logo-icon">⚡</span>
          <span>{appName}</span>
        </span>
      </div>

      <div className="top-header-right">
        <button
          className="th-icon-btn"
          title="Settings"
          onClick={() => navigate('/settings')}
        >
          <Settings size={18} />
        </button>

        <div className="th-notif-wrapper" ref={dropdownRef}>
          <button
            className="th-icon-btn"
            title="Notifications"
            onClick={() => setOpen((v) => !v)}
            aria-label={`${unreadCount} unread notifications`}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="th-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>

          {open && (
            <div className="th-notif-dropdown" role="dialog" aria-label="Notifications">
              <div className="th-notif-header">
                <span>Notifications</span>
                {unreadCount > 0 && (
                  <button className="th-mark-all-btn" onClick={handleMarkAllRead}>
                    Mark all read
                  </button>
                )}
              </div>

              <div className="th-notif-list">
                {notifications.length === 0 ? (
                  <div className="th-notif-empty">
                    <Bell size={32} />
                    <p>No notifications</p>
                  </div>
                ) : (
                  notifications.map((n) => (
                    <div
                      key={n.notificationId}
                      className={`th-notif-item ${n.isRead ? 'read' : 'unread'}`}
                    >
                      <div className="th-notif-dot" />
                      <div className="th-notif-content">
                        <p className="th-notif-title">{n.title}</p>
                        <p className="th-notif-message">{n.message}</p>
                        <span className="th-notif-time">{timeAgo(n.createdAt)}</span>
                      </div>
                      {!n.isRead && (
                        <button
                          className="th-read-btn"
                          title="Mark as read"
                          onClick={(e) => handleMarkAsRead(e, n.notificationId)}
                        >
                          <Check size={13} />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
